import moment from "moment";

import {
    LienWaiverStatusTypes,
    LienWaiverType,
} from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";

/** A bill split across two partial payments renders once per payment, so billId alone is not a row identity. */
export const getBillRowId = (bill: BillInfo) =>
    bill.merchantPaymentId === null
        ? String(bill.billId)
        : `${bill.billId}-${bill.merchantPaymentId}`;

export const isLienWaiverSigned = (bill: BillInfo) =>
    bill.lienWaiver?.status === LienWaiverStatusTypes.Approved ||
    bill.lienWaiver?.status === LienWaiverStatusTypes.BuilderApproved;

// Fields are optional because the two endpoints serving this page populate different subsets.
interface ILienWaiverInfoApi {
    lienWaiverId?: number | string;
    jobId?: number;
    title?: string;
    statusDate?: string;
    status?: LienWaiverStatusTypes;
    lienWaiverType?: LienWaiverType;
}

interface IBillInfoApi {
    billId: number;
    billNumber: string;
    billTitle: string;
    billAmount: number;
    paymentAmount?: number | null;
    jobName?: string;
    lienWaiver?: ILienWaiverInfoApi[] | null;
    lienWaiverExternal?: ILienWaiverInfoApi[] | null;
}

/** Mirrors Global.Core.Enums.LienWaivers.LienWaiverPayoutMethod. */
export enum LienWaiverPayoutMethod {
    SubSelects = 0,
    Ach = 1,
    MailedCheck = 2,
}

interface IPaymentInfoApi {
    merchantPaymentId: number;
    paymentAmount: number;
    bills: IBillInfoApi[];
    isPaid?: boolean;
    paidDate?: string;
    paymentMethodLastFour?: string;
}

interface ILienWaiverReviewResponseApi {
    payments?: IPaymentInfoApi[] | null;
    bills?: IBillInfoApi[];
    improvedSubLienWaiversEnabled?: boolean;
    builderName?: string;
    builderLogoUrl?: string;
}

export class LienWaiverInfo {
    constructor(data: ILienWaiverInfoApi) {
        this.lienWaiverId = data.lienWaiverId ?? 0;
        this.jobId = data.jobId ?? 0;
        this.title = data.title ?? "";
        this.statusDate = data.statusDate ? moment(data.statusDate) : null;
        this.status = data.status ?? LienWaiverStatusTypes.Unreleased;
        // Matches the column default. Unknown belongs to no section, so those rows would vanish.
        this.lienWaiverType = data.lienWaiverType ?? LienWaiverType.Conditional;
    }
    lienWaiverId: number | string;
    jobId: number;
    title: string;
    statusDate: moment.Moment | null;
    status: LienWaiverStatusTypes;
    lienWaiverType: LienWaiverType;
}

export class BillInfo {
    constructor(data: IBillInfoApi, merchantPaymentId: number | null = null) {
        this.merchantPaymentId = merchantPaymentId;
        this.billId = data.billId;
        this.billNumber = data.billNumber;
        this.billTitle = data.billTitle;
        this.billAmount = data.billAmount;
        this.paymentAmount = data.paymentAmount ?? null;
        this.jobName = data.jobName ?? "";
        const apiLienWaivers = data.lienWaiver ?? data.lienWaiverExternal ?? [];
        this.lienWaivers = apiLienWaivers.map((lw) => new LienWaiverInfo(lw));
        this.lienWaiver = this.lienWaivers[0] ?? null;
    }
    merchantPaymentId: number | null;
    billId: number;
    billNumber: string;
    billTitle: string;
    billAmount: number;

    /** What this row's payment releases, less than billAmount on a partial. Null when no payment is attached. */
    paymentAmount: number | null;
    jobName: string;

    /** Every waiver on the bill. A bill can carry one of each type at once. */
    lienWaivers: LienWaiverInfo[];

    /**
     * The waiver a single row acts on. Defaults to the first; sections rebind it to their own type via
     * {@link projectToType} so each per-type table shows that type's waiver.
     */
    lienWaiver: LienWaiverInfo | null;

    // Unreleased waivers haven't been sent to the sub yet, so they don't belong on the review list —
    // only a waiver the builder has released is theirs to act on.
    // TODO(multi-lw): returns the first released waiver of the type. Fine while a bill has one per type,
    // but once multiple conditionals are allowed this picks an arbitrary one and the row must key off
    // waiver id instead of type.
    releasedLienWaiverOfType(lienWaiverType: LienWaiverType): LienWaiverInfo | null {
        return (
            this.lienWaivers.find(
                (lw) =>
                    lw.lienWaiverType === lienWaiverType &&
                    lw.status !== LienWaiverStatusTypes.Unreleased,
            ) ?? null
        );
    }

    /** A copy of this bill whose singular lienWaiver is the given type's waiver, for a per-type section row. */
    projectToType(lienWaiverType: LienWaiverType): BillInfo {
        const projected = Object.assign(Object.create(BillInfo.prototype) as BillInfo, this);
        projected.lienWaiver = this.releasedLienWaiverOfType(lienWaiverType);
        return projected;
    }
}

export class PaymentInfo {
    constructor(data: IPaymentInfoApi) {
        this.merchantPaymentId = data.merchantPaymentId;
        this.paymentAmount = data.paymentAmount;
        this.bills = data.bills.map((p) => new BillInfo(p, data.merchantPaymentId));
        this.isPaid = data.isPaid ?? false;
        this.paidDate = data.paidDate ? moment(data.paidDate) : null;
        this.paymentMethodLastFour = data.paymentMethodLastFour ?? null;
    }
    merchantPaymentId: number;
    paymentAmount: number;
    bills: BillInfo[];
    isPaid: boolean;
    paidDate: moment.Moment | null;
    paymentMethodLastFour: string | null;
}

export class LienWaiverReviewResponse {
    constructor(data: ILienWaiverReviewResponseApi) {
        this.payments = data.payments?.map((m) => new PaymentInfo(m)) ?? null;
        this.bills = data.bills?.map((x) => new BillInfo(x)) ?? [];
        this.improvedSubLienWaiversEnabled = data.improvedSubLienWaiversEnabled ?? false;
        this.builderName = data.builderName ?? "";
        this.builderLogoUrl = data.builderLogoUrl ?? "";
    }
    payments: PaymentInfo[] | null;
    bills: BillInfo[];
    /** The anonymous sub-facing page has no BuilderInfoContext, so the flag arrives on the response. */
    improvedSubLienWaiversEnabled: boolean;
    builderName: string;
    builderLogoUrl: string;

    /**
     * The legacy flat list: payment-linked and standalone bills merged, de-duplicated so a bill on two
     * pending payments is counted once.
     */
    get allBills(): BillInfo[] {
        const paymentBills = this.payments?.flatMap((payment) => payment.bills) ?? [];
        const byBillId = new Map<number, BillInfo>();
        [...paymentBills, ...this.bills].forEach((bill) => {
            if (!byBillId.has(bill.billId)) {
                byBillId.set(bill.billId, bill);
            }
        });
        return [...byBillId.values()];
    }

    /**
     * A bill linked to more than one pending payment stays under every payment it belongs to - the
     * sub is choosing which payment to release, so collapsing duplicates would hide one.
     */
    getSections(): ILienWaiverSection[] {
        return [
            this.buildSection(LienWaiverType.Conditional),
            this.buildSection(LienWaiverType.Unconditional),
        ].filter((section) => section.paymentGroups.length > 0 || section.unlinkedBills.length > 0);
    }

    /** Lets a row show how it was paid in the unconditional section, where payment grouping is flattened away. */
    getPaymentContextByRowId(): Map<string, IBillPaymentContext> {
        const contextByRowId = new Map<string, IBillPaymentContext>();

        (this.payments ?? []).forEach((payment) => {
            payment.bills.forEach((bill) => {
                contextByRowId.set(getBillRowId(bill), {
                    isPaid: payment.isPaid,
                    paidDate: payment.paidDate,
                    paymentMethodLastFour: payment.paymentMethodLastFour,
                });
            });
        });

        return contextByRowId;
    }

    private buildSection(lienWaiverType: LienWaiverType): ILienWaiverSection {
        // A bill carrying a released waiver of each type appears in both sections, each row bound to
        // that section's waiver.
        const forType = (bills: BillInfo[]) =>
            bills
                .filter((bill) => bill.releasedLienWaiverOfType(lienWaiverType) !== null)
                .map((bill) => bill.projectToType(lienWaiverType));

        // The payment-token endpoint returns a payment's bills in both payments[] and bills[], so a bill
        // already shown under its payment must not render again as unlinked. The list endpoint keeps the
        // two arrays disjoint, where this filter removes nothing.
        const billIdsInPayments = new Set(
            (this.payments ?? []).flatMap((payment) => payment.bills.map((bill) => bill.billId)),
        );

        return {
            lienWaiverType,
            paymentGroups: (this.payments ?? [])
                .map((payment) => {
                    const bills = forType(payment.bills);

                    return {
                        merchantPaymentId: payment.merchantPaymentId,
                        paymentAmount: payment.paymentAmount,
                        bills,
                        signedCount: bills.filter(isLienWaiverSigned).length,
                        isPaid: payment.isPaid,
                        paidDate: payment.paidDate,
                        paymentMethodLastFour: payment.paymentMethodLastFour,
                    };
                })
                .filter((group) => group.bills.length > 0),
            unlinkedBills: forType(
                this.bills.filter((bill) => !billIdsInPayments.has(bill.billId)),
            ),
        };
    }
}

export interface ILienWaiverPaymentGroup {
    merchantPaymentId: number;
    paymentAmount: number;
    bills: BillInfo[];
    signedCount: number;
    isPaid: boolean;
    paidDate: moment.Moment | null;
    paymentMethodLastFour: string | null;
}

export interface ILienWaiverSection {
    lienWaiverType: LienWaiverType;
    paymentGroups: ILienWaiverPaymentGroup[];
    unlinkedBills: BillInfo[];
}

export interface IBillPaymentContext {
    isPaid: boolean;
    paidDate: moment.Moment | null;
    paymentMethodLastFour: string | null;
}

export interface ILienWaiverPaymentContext {
    merchantPaymentId: number;
    paymentAmount: number;
    paymentShareToken: string | null;
    payoutMethod: LienWaiverPayoutMethod;
    payoutDestinationLastFour: string | null;
    waiverCount: number;

    /** Signed before this waiver: the payment releases once this reaches waiverCount. */
    signedWaiverCount: number;
}

/** True once this waiver is the only one left holding its payment back. */
export const isLastWaiverOnPayment = (context: ILienWaiverPaymentContext) =>
    context.signedWaiverCount >= context.waiverCount - 1;

export interface ILienWaiverReviewRequest {
    encodedBuilderId: string;
    encodedSubId: string;
    shareToken?: string;
}
